/**
 * Every command the simulation accepts, and the single entry point `apply`.
 * One file per command in this folder. Add commands with
 * `pnpm gen command <name>`; it inserts at the `gen:` markers below.
 */
import { sortedKeys } from '../determinism.ts';
import { InvalidCommand, type RunState } from '../state.ts';
import type { ApplyResult, Ctx } from './types.ts';
import { type WaitCommand, wait } from './wait.ts';
import { type EnterFloorCommand, enterFloor } from './enter_floor.ts';
import { type TurnCommand, turn } from './turn.ts';
import { type MoveCommand, move } from './move.ts';
import { type AttackCommand, attack } from './attack.ts';
import { type DescendCommand, descend } from './descend.ts';
import { type UseSkillCommand, useSkill } from './use_skill.ts';
import { type UseItemCommand, useItem } from './use_item.ts';
// gen:command-imports

export type Command =
  | WaitCommand
  | EnterFloorCommand
  | TurnCommand
  | MoveCommand
  | AttackCommand
  | DescendCommand
  | UseSkillCommand
  | UseItemCommand
  // gen:command-union
;

/**
 * The payload keys each command type may carry besides `type`. A command
 * with any other key is refused, so a log cannot carry fields that a later
 * version would read. Each handler checks the values of its keys.
 */
const commandKeys: { readonly [C in Command as C['type']]: readonly Exclude<keyof C, 'type'>[] } = {
  wait: [],
  enter_floor: ['seed'],
  turn: ['dir'],
  move: ['dir'],
  attack: [],
  descend: [],
  use_skill: ['skill', 'dir', 'target'],
  use_item: ['item', 'dir', 'target'],
  // gen:command-keys
};

const commandTypes: readonly string[] = sortedKeys(commandKeys);

/**
 * Throws `InvalidCommand` unless `command` is a plain object whose `type` is a
 * known command type and whose other keys are all declared for that type.
 * Logs arrive as JSON, so this runs on every command whatever its static type.
 */
const assertCommandShape = (command: unknown): void => {
  if (typeof command !== 'object' || command === null || Array.isArray(command)) throw new InvalidCommand('command_not_object', 'a command is an object');
  const proto: unknown = Object.getPrototypeOf(command);
  if (proto !== Object.prototype && proto !== null) throw new InvalidCommand('command_not_plain', 'a command is a plain object');
  const fields = command as Record<string, unknown>;
  const type = fields['type'];
  if (typeof type !== 'string' || !commandTypes.includes(type)) throw new InvalidCommand('unknown_command_type', `unknown command type ${String(type)}`, { type: String(type) });
  const allowed: readonly string[] = commandKeys[type as Command['type']];
  for (const key of sortedKeys(fields)) {
    if (key !== 'type' && !allowed.includes(key)) throw new InvalidCommand('command_unknown_field', `${type} takes no field ${key}`, { command: type, field: key });
  }
};

/**
 * Apply one command to a run. Pure: same content, state and command in, same
 * result out. Throws `InvalidCommand` when the run was started with other
 * content, the command is malformed, or the rules do not allow it.
 */
export const apply = (ctx: Ctx, state: RunState, command: Command): ApplyResult => {
  if (state.contentHash !== ctx.content.hash) throw new InvalidCommand('content_mismatch', 'the run was started with different content');
  assertCommandShape(command);
  switch (command.type) {
    case 'wait':
      return wait(ctx, state, command);
    case 'enter_floor':
      return enterFloor(ctx, state, command);
    case 'turn':
      return turn(ctx, state, command);
    case 'move':
      return move(ctx, state, command);
    case 'attack':
      return attack(ctx, state, command);
    case 'descend':
      return descend(ctx, state, command);
    case 'use_skill':
      return useSkill(ctx, state, command);
    case 'use_item':
      return useItem(ctx, state, command);
    // gen:command-handlers
  }
};
